import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Navbar } from "@/components/Navbar";
import { AuthImage } from "@/components/AuthImage";
import { PullToRefresh } from "@/components/PullToRefresh";
import { Plus, Eye, Clock, CheckCircle2, FileImage, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function OwnerDashboard() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    try {
      const res = await api.get("/issues");
      setIssues(res.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const repost = async (e, issueId) => {
    e.preventDefault(); e.stopPropagation();
    try {
      await api.post(`/issues/${issueId}/repost`);
      toast.success("Job reposted — back in shop feeds for 7 days.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Repost failed");
    }
  };

  const stats = {
    total: issues.length,
    open: issues.filter((i) => i.status === "open").length,
    in_progress: issues.filter((i) => i.status === "in_progress").length,
    completed: issues.filter((i) => i.status === "completed").length,
  };

  return (
    <div className="min-h-screen bg-[#09090B]">
      <Navbar />

      <PullToRefresh onRefresh={reload}>
        <main className="max-w-[1400px] mx-auto px-4 sm:px-8 lg:px-12 py-10" data-testid="owner-dashboard">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <div className="label-eyebrow mb-3">OWNER DASHBOARD</div>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter">Your garage.</h1>
            <p className="text-zinc-400 mt-2">Every issue you post is anonymous until you choose a shop.</p>
          </div>
          <Link to="/post" className="btn-primary flex items-center gap-2" data-testid="btn-new-issue">
            <Plus className="w-4 h-4" /> New Issue
          </Link>
        </div>

        {/* Bento stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-12">
          {[
            { label: "TOTAL ISSUES", value: stats.total },
            { label: "OPEN", value: stats.open, accent: true },
            { label: "IN PROGRESS", value: stats.in_progress },
            { label: "COMPLETED", value: stats.completed },
          ].map((s) => (
            <div key={s.label} className="bg-[#09090B] p-6">
              <div className="label-eyebrow mb-3">{s.label}</div>
              <div className={`font-heading text-4xl font-black ${s.accent ? "text-[#FF3B30]" : ""}`}>{s.value}</div>
            </div>
          ))}
        </div>

        <h2 className="font-heading text-2xl font-bold tracking-tight mb-6">Your Issues</h2>

        {loading ? (
          <div className="text-zinc-500">Loading...</div>
        ) : issues.length === 0 ? (
          <div className="border border-dashed border-white/10 p-12 text-center">
            <FileImage className="w-10 h-10 text-zinc-700 mx-auto mb-4" strokeWidth={1.2} />
            <div className="label-eyebrow mb-3 text-zinc-500">NO ISSUES YET</div>
            <p className="text-zinc-400 mb-6">Post your first issue to start receiving private quotes.</p>
            <Link to="/post" className="btn-primary inline-flex items-center gap-2" data-testid="empty-new-issue">
              <Plus className="w-4 h-4" /> New Issue
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {issues.map((i) => {
              const isExpired = i.status === "expired" || i.expired;
              return (
                <Link to={`/issue/${i.issue_id}`} key={i.issue_id} className="card-interactive p-0 overflow-hidden block" data-testid={`issue-card-${i.issue_id}`}>
                  <div className="aspect-video bg-zinc-900 relative">
                    {i.file_paths?.[0] ? (
                      <AuthImage path={i.file_paths[0]} className={`w-full h-full object-cover ${isExpired ? "grayscale opacity-60" : ""}`} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-zinc-700">
                        <FileImage className="w-10 h-10" strokeWidth={1.2} />
                      </div>
                    )}
                    <StatusBadge status={isExpired ? "expired" : i.status} />
                  </div>
                  <div className="p-5">
                    <div className="label-eyebrow text-zinc-500 mb-2">{i.vehicle_make || "Unknown"} · {i.vehicle_year || "—"}</div>
                    <h3 className="font-heading text-base font-bold mb-2 line-clamp-1">{i.title}</h3>
                    <p className="text-sm text-zinc-400 line-clamp-2">{i.description}</p>
                    {isExpired && (
                      <div className="mt-4 border border-[#FF9500]/40 bg-[#FF9500]/5 p-3 flex items-start gap-2 text-xs">
                        <AlertTriangle className="w-4 h-4 text-[#FF9500] shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <div className="text-[#FF9500] font-semibold mb-2">Shops can no longer see this</div>
                          <button onClick={(e) => repost(e, i.issue_id)} className="btn-secondary text-xs py-1.5 flex items-center gap-1.5" data-testid={`repost-${i.issue_id}`}>
                            <RefreshCw className="w-3 h-3" /> Repost for 7 more days
                          </button>
                        </div>
                      </div>
                    )}
                    {i.ai_category && !isExpired && (
                      <div className="mt-4 flex gap-2">
                        <span className="text-xs uppercase tracking-widest border border-[#007AFF]/40 text-[#007AFF] px-2 py-1">{i.ai_category}</span>
                        {i.ai_severity && <span className="text-xs uppercase tracking-widest border border-white/10 text-zinc-400 px-2 py-1">{i.ai_severity}</span>}
                      </div>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
        </main>
      </PullToRefresh>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    open: { label: "OPEN", c: "bg-[#FF3B30] text-white", icon: Eye },
    in_progress: { label: "IN PROGRESS", c: "bg-[#FF9500] text-black", icon: Clock },
    completed: { label: "COMPLETED", c: "bg-[#34C759] text-black", icon: CheckCircle2 },
    closed: { label: "CLOSED", c: "bg-zinc-700 text-white", icon: CheckCircle2 },
    expired: { label: "EXPIRED", c: "bg-zinc-700 text-zinc-300", icon: AlertTriangle },
  };
  const x = map[status] || map.open;
  const Icon = x.icon;
  return (
    <span className={`absolute top-3 left-3 ${x.c} text-xs font-bold uppercase tracking-widest px-2 py-1 flex items-center gap-1.5`}>
      <Icon className="w-3 h-3" strokeWidth={2.5} /> {x.label}
    </span>
  );
}
